import { action } from '@ember/object';
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type Drumset from 'editor/models/drumset';
import type { Instrument } from 'editor/models/drumset';
import type { SongTrack } from 'editor/models/song';
import constrainValue from 'editor/utils/constrain-value';
import List from 'editor/utils/list';
import MusicUtils from 'editor/utils/music-utils';
import type ProjectManagerService from './project-manager';
import type Song from 'editor/models/song';
import playInstrument from 'editor/utils/play-instrument';
import playAccent from 'editor/utils/play-accent';
import type SongPlayerService from './song-player';


export class ChokeGroup {
  notes: Array<AudioBufferSourceNode> = [];
  notesAndNames: Array<[AudioBufferSourceNode, string]> = [];

  choke(when: number, instrumentName: string) {
    // Sets the note to stop and then removes the note so the next choke doesn’t update this note’s stop time
    this.notes = this.notes.filter((note) => {
      const result = this.notesAndNames.find(([n]) => n === note);
      if (result && result[1] != instrumentName) {
        // We only want to stop the note if the note isn't the same one that is currently playing
        note.stop(when);
        return false;
      } else {
        return true;
      }
    });
  }

  add(note: AudioBufferSourceNode, name: string) {
    this.notes.push(note);
    this.notesAndNames.push([note, name]);
  }
}

interface PlayOpts {
  loop?: boolean;
  playFrom?: any;
  startOffset?: any;
  followedBy?: any;
  nextBar?: boolean;
}

type QueuedTrack = {
  type: TrackType;
  track: SongTrack;
  loop: boolean;
  playFrom?: number;
  startOffset?: number;
  followedBy?: QueuedTrack;
};

export enum TrackType {
  INTRO,
  OUTRO,
  MAIN,
  FILL,
  TRANSITION,
  PAUSED,
}

export default class VirtualPedalService extends Service {
  @service declare projectManager: ProjectManagerService;
  @service declare songPlayer: SongPlayerService;

  audioContext: AudioContext;
  drumsetVolume: GainNode;
  @tracked isPlaying = false;
  @tracked currentTrack?: SongTrack;
  @tracked currentTrackType?: TrackType;
  @tracked beat = 0;
  @tracked bar = 0;
  @tracked _currentSong?: Song;
  @tracked _currentDrumset?: Drumset;
  @tracked tickAtWhichPartStarted = 0;
  trackQueue = new List<QueuedTrack>();
  apEvents: number[] = [];

  constructor(properties?: object) {
    super(properties);
    this.audioContext = new AudioContext({
      latencyHint: 'interactive',
      sampleRate: 44100,
    });
    this.drumsetVolume = new GainNode(this.audioContext, { gain: 1 });
    this.drumsetVolume.connect(this.audioContext.destination);
    this.projectManager.on('songChange', (songFile) => {
      songFile.parse().then((song) => {
        if (song) this.song = song;
      });
    });
    this.projectManager.currentSong?.parse().then((song) => {
      if (song) this.song = song;
    });
  }

  set song(song: Song) {
    this.stop();
    this._currentSong = song;
    this.setDrumset(song.drumset);
    song.on('changeDrumset', (drumsetName) => {
      this.setDrumset(drumsetName);
    });
    this.audioContext.suspend();
  }

  get song() {
    return this._currentSong!;
  }

  setSong(song: Song) {
    this._currentSong = song;
  }

  setDrumset(name: string) {
    this.projectManager
      .findDrumset(name)
      .parse()
      .then((drumset) => {
        if (drumset) {
          drumset.prepareAudio(this.audioContext);
          this._currentDrumset = drumset;
          const drumsetGain = drumset.volume / 100;
          this.drumsetVolume.gain.setValueAtTime(
            drumsetGain,
            this.audioContext.currentTime,
          );
        }
      });
  }

  get isTransitioningWithoutFile() {
    return this.currentPart!.info.autoPilotEnabled && this.song.autoPilotEnabled && // only relevant for autopilot
      !this.currentPart!.transition && // only relevant if part doesn't have a transition file
          (this.currentPartIndex < this.song.parts.length - 1 || this.song.autoPilotLoop) && // on a part that will transition
           (this.totalTicksPerSong! - this.tickAtWhichPartStarted // ticks into part - totalTicksPerSong is the total, but we want to know how many ticks into the part we are 
            + this.currentPart!.tracks[0]!.barLength) // one bar later, will we be at the end of the part?
            > (this.currentPart!.tracks[0]!.barLength * this.currentPart!.info.playFor) // in the last bar, where the "transition" will happen
  }

  get drumset() {
    return this._currentDrumset;
  }

  get label() {
    switch (this.currentTrackType) {
      case TrackType.INTRO:
        return 'Intro fill';
      case TrackType.MAIN:
      case TrackType.TRANSITION:
      case TrackType.PAUSED:
        return `Part ${this.currentPartIndex + 1}/${this.song.parts.length}`;
      case TrackType.FILL:
        return `Fill ${this.currentFillIndex + 1}/${this.currentPart!.fills.length}`;
      case TrackType.OUTRO:
        return 'Outro fill';
      default:
        return '';
    }
  }

  get subLabel() {
    switch (this.currentTrackType) {
      case TrackType.INTRO:
        return '';
      case TrackType.MAIN:
        if (this.isTransitioningWithoutFile) {
          return 'Transition';
        }
        return ``;
      case TrackType.FILL:
        return ``;
      case TrackType.TRANSITION:
        return 'Transition';
      case TrackType.PAUSED:
        return 'Paused';
      case TrackType.OUTRO:
        return '';
      default:
        return '';
    }
  }

  get ticksPerSecond() {
    const track = this.currentTrack ?? this.song.tracks[0]!;
    return (track.tickToQuarter / 60) * this.song.tempo;
  }

  get secondsPerBeat() {
    if (!this.currentTrack) return 0;

    return (
      this.currentTrack.tickToQuarter /
      (this.currentTrack.timeSignatureDenominator / 4) /
      this.ticksPerSecond
    );
  }

  get secondsPerSixteenth() {
    if (!this.currentTrack) return 0;
    const ticksToSixteenth = this.currentTrack?.tickToQuarter / 4;
    return ticksToSixteenth / this.ticksPerSecond;
  }

  #chokeGroups = new Array(15).fill(0).map(() => new ChokeGroup());
  getChokeGroup(group: number): ChokeGroup {
    if (group < 0 || group > 15) throw new Error('Invalid choke group');
    return this.#chokeGroups[group]!;
  }

  addToChokeGroup(group: number, note: AudioBufferSourceNode, name: string) {
    this.getChokeGroup(group).add(note, name);
  }

  #fillChokeGroups = new Array(15).fill(0).map(() => new Array<number>());
  getFillChokeGroup(group: number): number[] {
    if (group < 0 || group > 15) throw new Error('Invalid choke group');
    return this.#fillChokeGroups[group]!;
  }

  addToFillChokeGroup(group: number, onWhen: number) {
    this.getFillChokeGroup(group).push(onWhen);
  }

  clearFillChokeGroups() {
    this.#fillChokeGroups = new Array(15)
      .fill(0)
      .map(() => new Array<number>());
  }

  start(partIndex = 0, skipIntro = false) {
    if (this.isPlaying) return;

    this.totalTicksPerSong = 0;
    this.songStartTime = this.audioContext.currentTime;

    this.currentTrackType = TrackType.INTRO;

    clearTimeout(this.loopTimer);
    this.clearQueue();

    this.isPlaying = true;
    this.songPlayer.stop();

    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }

    // auto pilot mode
    if (this.song.intro && this.song.intro.main && !skipIntro) {
      if (this.song.autoPilotEnabled) {
        const ticks = this.song.intro.main!.tickCount; // intro can be partial, use tickCount, not barCount * barLength
        this.setupAutoPilotForSinglePart(0, ticks);
      }
      this.queue(TrackType.INTRO, this.song.intro.main!, {
        loop: false,
        playFrom: 0,
        startOffset: this.song.intro.main!.trigPos,
        followedBy: {
          type: TrackType.MAIN,
          track: this.song.parts[partIndex]!.main!,
          loop: true,
        },
      });
    } else {
      if (this.song.autoPilotEnabled) {
        const ticks = this.totalTicksPerSong;
        this.setupAutoPilotForSinglePart(0, ticks);
      }
      this.playTrack(this.song.parts[0]!.main!, TrackType.MAIN, true);
    }

    this.tick();
  }

  setupAutoPilotForSinglePart(nextPartIndex: number = 0, globalTicks: number = 0) {
    /*
    IMPORTANT NOTE: this.song.info.parts[i].timeSignatureNumerator/Denonimator is NOT correct if it's ever changed.
                    Instead, you have to go to this.song.parts[i].timeSignatureNumerator/Denonimator - this is correct.
    HOWEVER:        You must use this.song.info.parts[i].playFor/transitionPlayFor/drumFillPlayAt[j] for those values.
                    To get ticks per measure you must do this.song.parts[i].tracks[j].barLength/barCount
    IN SUM:
                    time sig num/denom: this.song.parts[i].timeSignatureNumerator/Denonimator
                    AP playFor/At nums: this.song.info.parts[i].playFor / transitionPlayFor / drumFillPlayAt[j]
                    sng has transition: this.song.info.parts[i].transitionFileIndex != -1
                    #ticks per measure: this.song.parts[i].tracks[j].barLength/barCount

    NO GUARANTEE OF ACCURACY ANYWHERE ELSE

    lord save us all...
    */
    this.apEvents = [];
    this.numberOfFillsPlayed = 0;
    this.manualStopWhileAutoPilot = false;
    var totalTicks = globalTicks;
    this.tickAtWhichPartStarted = globalTicks;
    // The below are because tick() doesn't get called every midi tick - it's closer to every 10-30 midi ticks.
    const fillDelay = 30; // we want the fills to start slightly later
    const transitionDelay = 100; // we want the transitions to start slightly earlier
    const outroDelay = -7; // we want the outro to play slightly later

    const part = this.song.parts[nextPartIndex]!;
    if (!part.info.autoPilotEnabled) {
      // If a part doesn't have auto pilot enabled, we don't want to add it to the auto pilot queue
      return;
    }
    const ticksPerBarMainTrack = part.tracks[0]!.barLength; // tracks[0] is always main track, even if you first add fills/transition, then add the main
    // now unused - now we use tickCount. // const ticksPerBarTransition = part.tracks[part.tracks.length - 1]!.barLength; // transition is always last track, even if created before main/fills
    const numFills = part.fills.length;
    const fillPlayAtNumbers = this.song!.info!.parts[nextPartIndex]!.drumFillPlayAt; // since the array is zero indexed but playAt 1 means the first measure (aka, the 0th), we need to subtract 1 when it's used
    const partPlayFor = this.song!.info!.parts[nextPartIndex]!.playFor;

    for (let fillPlayAt = 0; fillPlayAt < numFills; fillPlayAt++) {
      // Add each fill to the events array to be played
      // Fills are played in place of the part measure(s) not in addition to
      if (fillPlayAtNumbers[fillPlayAt] == 0) {
        // If the fill is set to play at 0, we don't want to add it to the auto pilot queue
        continue;
      }
      this.apEvents.push(
        (fillPlayAtNumbers[fillPlayAt]! - 1) * ticksPerBarMainTrack +
          totalTicks +
          fillDelay,
      );
      // we add 30 ticks to ensure the fill starts after the measure starts. If we didn't do this, and had a fill play immediately after the intro, it would get returned in triggerFill() because of if (this.currentTrackType === TrackType.INTRO) return;
    }

    const hasTransition =
      this.song.info.parts[nextPartIndex]!.transitionFileIndex != -1;
    if (
      hasTransition &&
      (nextPartIndex != this.song.info.parts.length - 1 ||
        (nextPartIndex != this.song.info.parts.length && this.song.autoPilotLoop))
    ) {
      // If a part is has a transition add each transition to the array to be played
      // The transitions are played in place of the part measure(s) not in addition to
      // If we are on the last part, only add the transition if we are looping the whole song
      this.apEvents.push(
        partPlayFor * ticksPerBarMainTrack -
          part.tracks[part.tracks.length - 1].tickCount + // transitions can be partial, use tickCount, not barCount * barLength
          totalTicks,
      );
    }

    // This is a bit odd, but we need to add a small delay to make sure we start/stop the
    // transitions/outro to make sure we aren't a measure late
    // Use transitionDelay for all parts that have a transition to play, including the last part when looping
    const isLastPart = nextPartIndex === this.song.info.parts.length - 1;
    const hasTransitionToPlay = !isLastPart || this.song.autoPilotLoop;
    const preemptiveTransition = hasTransitionToPlay ? transitionDelay : outroDelay;

    let outroPlayFor = 0;
    if (
      this.song!.outro!.main! &&
      !this.song.autoPilotLoop &&
      nextPartIndex == this.song.info.parts.length - 1 &&
      !this.song.autoPilotOutroPlayAfter
    ) {
      // If we are on the last part and we are not looping the song, we need to add the outro play for
      // because the outro also replaces measures in the main part instead of adds measures on if the
      // setting says to not play after the last part
      outroPlayFor =
        this.song!.info!.outro.playFor * this.song.outro.main!.barLength;
    }

    // This is adding the odd delay to the events array
    this.apEvents.push(
      partPlayFor * ticksPerBarMainTrack +
        totalTicks -
        preemptiveTransition -
        outroPlayFor,
    ); // - 100 ticks to ensure the transition end gets queued before it starts another measure

    totalTicks += partPlayFor * ticksPerBarMainTrack; // not sure if this is accurate for multiple time sigs within a part
    console.log('Auto pilot events1', this.apEvents);
  }

  getAutoPilotTempoTimeSigNumber(
    trackType: TrackType,
    partIndex?: number,
    fillIndex?: number,
  ) {
    const tempoMultiplier = 240 / this.song.tempo;
    switch (trackType) {
      case TrackType.INTRO:
        return (
          tempoMultiplier *
          (this.song.intro.timeSignatureNumerator /
            this.song.intro.timeSignatureDenominator)
        );
      case TrackType.MAIN:
        return (
          tempoMultiplier *
          (this.song.parts[partIndex!]!.timeSignatureNumerator /
            this.song.parts[partIndex!]!.timeSignatureDenominator)
        );
      case TrackType.FILL:
        return (
          tempoMultiplier *
          (this.song.parts[partIndex!]!.fills[fillIndex!]!.timeSignatureNumerator /
            this.song.parts[partIndex!]!.fills[fillIndex!]!.timeSignatureDenominator)
        );
      case TrackType.TRANSITION:
        return (
          tempoMultiplier *
          (this.song.parts[partIndex!]!.transition!.timeSignatureNumerator /
            this.song.parts[partIndex!]!.transition!.timeSignatureDenominator)
        );
      case TrackType.OUTRO:
        return (
          tempoMultiplier *
          (this.song.outro.timeSignatureNumerator /
            this.song.outro.timeSignatureDenominator)
        );
      default:
        console.warn('Invalid track type, how did we get here?', trackType, partIndex, fillIndex);
        return 1;
    }
  }

  stop() {
    this.unpause();
    this.numberOfFillsPlayed = 0;
    this.isPlaying = false;
    this.clearQueue();
    if (this.tickTimer) {
      cancelAnimationFrame(this.tickTimer);
    }
    if (this.loopTimer) {
      clearTimeout(this.loopTimer);
    }
    if (this.followUpTimer) {
      clearTimeout(this.followUpTimer);
    }
    if (this.trackMarkerTimer) {
      clearTimeout(this.trackMarkerTimer);
    }
    this.currentTrackType = undefined;
    this.currentTrack = undefined;
    this.currentFillIndex = -1;
    this.currentPartIndex = 0;
    this.transitionToPart = 0;
    this.totalTicksPerSong = 0;
    this.songStartTime = 0;
    this.playingEvents.forEach(([noteOn, note]) => {
      if (noteOn >= this.audioContext.currentTime) {
        note.stop();
      }
    });
    this.playingEvents.clear();
  }

  /**
   * Adds a track to play after the current track
   */
  queue(type: TrackType, track: SongTrack, playOpts: PlayOpts) {
    const loop = playOpts.loop ?? false;
    const playFrom =
      playOpts.playFrom ?? (playOpts.nextBar === true ? -1 : undefined);
    const startOffset = playOpts.startOffset;
    const followedBy = playOpts.followedBy;
    this.trackQueue.clear();
    if (
      this.song.autoPilotEnabled &&
      this.trackQueue.length > 1 &&
      this.trackQueue.tail!.data.playFrom === playFrom
    ) {
      // If the last track in the queue has the same playFrom time,
      // remove the last item and replace it with the new item
      this.trackQueue.pop();
    }

    this.trackQueue.push({
      type,
      track,
      loop,
      playFrom,
      startOffset,
      followedBy,
    });
  }

  clearQueue() {
    this.trackQueue.clear();
  }

  tickTimer?: number;
  followUpTimer?: number;
  autoPilotTrackEnd?: number;
  manualStopWhileAutoPilot?: boolean;
  totalTicksPerSong?: number;
  songStartTime?: number;
  @action
  tick() {
    if (!this.isPlaying) return;

    // Snapshot of current time
    const currentTime = this.audioContext.currentTime;

    // If the outro is complete, then stop
    if (
      this.currentTrackType === TrackType.OUTRO &&
      currentTime >= this.trackEnd
    ) {
      this.stop();
      return;
    }

    // Set up beat information for the UI
    const track = this.currentTrack;
    let barEnd = 0;
    if (track) {
      const elapsedTime = currentTime - this.trackStart;
      const songElapsedTime = currentTime - this.songStartTime!;
      const timePerTick = (this.trackEnd - this.trackStart) / track.totalTicks;

      let currentTick = Math.round(elapsedTime / timePerTick);
      this.totalTicksPerSong = Math.round(songElapsedTime / timePerTick);
      if (currentTick === Infinity) {
        currentTick = 0;
      }
      if (currentTick < 0) {
        currentTick = track.totalTicks + currentTick;
      }
      const beatTicks: number = MusicUtils.beatTicks(
        track.tickToQuarter,
        track.timeSignatureDenominator,
      );
      const beat = Math.floor(
        (currentTick / beatTicks) % track.timeSignatureNumerator,
      );
      if (beat != this.beat && !isNaN(beat)) this.beat = beat;
      const bar = Math.floor(currentTick / track.barLength);
      if (bar != this.bar && !isNaN(bar)) this.bar = bar;
      barEnd =
        this.trackStart + ((bar + 1) * track.barLength) / this.ticksPerSecond;
    }

    // If we have a queued track and we’re within the last half beat of the current track
    if (this.trackQueue.first) {
      if (
        // The next track is queued to play from the end of the curren track
        (!this.trackQueue.first.playFrom &&
          this.trackEnd - currentTime <= this.secondsPerBeat / 2) ||
        // The next track is queued to play from a specific time
        (typeof this.trackQueue.first.playFrom !== 'undefined' &&
          this.trackQueue.first.playFrom - this.secondsPerBeat / 2 <=
            currentTime)
      ) {
        // Grab the first track from the queue
        let { type, track, loop, playFrom, startOffset, followedBy } =
          this.trackQueue.shift()!;
        playFrom = playFrom ?? this.trackEnd;
        if (playFrom === -1) {
          playFrom = barEnd;
        }

        // Clear the loop timer so it doesn’t continue to buffer the currently playing track
        // Clear other timers to avoid issues like https://github.com/SingularSound/BBMO/issues/39
        clearTimeout(this.loopTimer);
        clearTimeout(this.trackMarkerTimer);
        clearTimeout(this.followUpTimer);

        // If the incoming track is not a main track, then we need to stop any notes that are scheduled to play in the buffer
        if (type !== TrackType.MAIN || this.song.autoPilotEnabled) {
          // If auto pilot is on we always want to stop the previous part from playing
          // Stop all notes scheduled after this track is scheduled to start
          var extraNotes = 0;
          for (let i = 0; i < this.currentTrack?.events?.length; i++) {
            const event = this.currentTrack?.events[i];
            if (event.tick >=  (this.currentTrack?.tickCount)) {
              extraNotes++;
            }
          }
          var dropped = this.playingEvents.dropAfter(
            ([noteOn]) => noteOn < playFrom!,
          );
          var inBarDropped = []; // in bar dropped - only drop the notes within the bar (so don't drop extraTick notes)
          for (let i = 0; i < dropped.length; i++) {
            if (i < extraNotes) {
              continue; // this assumes that the extraTick notes (the notes beyond the end of the track) are the first notes in the dropped array. Not sure if true, but so far in all testing it has been
            }
            inBarDropped.push(dropped[i]);
          }
          inBarDropped.forEach(([, note]) => note.stop());
        }

        // Set and play the new track
        this.playTrack(track, type, loop, playFrom, startOffset ?? 0);
        // A bit of a hack to make sure the above track is buffered first
        clearTimeout(this.followUpTimer);
        this.followUpTimer = setTimeout(() => {
          if (followedBy) {
            this.queue(followedBy.type, followedBy.track, {
              loop: followedBy.loop,
              startOffset: followedBy.startOffset,
            });
          }
        }, 100);
      }
    }
    this.tickTimer = requestAnimationFrame(this.tick);
    this.autoPilotHelper();
  }

  numberOfFillsPlayed = 0;
  autoPilotHelper() {
    if (!this.song.autoPilotEnabled) {
      return;
    }
    // If we have another element in the array and we are past the ticks, play that part
    if (
      this.apEvents.length > 0 &&
      this.apEvents[0]! < this.totalTicksPerSong!
    ) {
      console.log('Auto pilot event2', this.apEvents[0], this.totalTicksPerSong);
      this.apEvents.shift();
      if (this.manualStopWhileAutoPilot) {
        return;
      }
      if (
        this.currentPart!.info.drumFillPlayAt.filter((playAtNum) => playAtNum > 0).length > 0 &&
        this.numberOfFillsPlayed < this.currentPart!.info.drumFillPlayAt.filter((playAtNum) => playAtNum > 0).length
      ) {
        // If there is a fill to be played, play it
        this.triggerFill(true);
        this.numberOfFillsPlayed++;
      } else if (
        this.currentPart?.transition &&
        (this.currentPartIndex < this.song.parts.length - 1 ||
          (this.currentPartIndex < this.song.parts.length &&
            this.song.autoPilotLoop)) &&
        this.currentTrackType !== TrackType.TRANSITION
      ) {
        // If there is a transition to be played, play it
        this.triggerStartTransition(127, true);
      } else if (this.currentTrackType === TrackType.TRANSITION) {
        this.numberOfFillsPlayed = 0;
        // If we are in a transition, end it
        this.triggerEndTransition();
      } else if (
        (this.currentPartIndex < this.song.parts.length - 1 ||
          (this.currentPartIndex < this.song.parts.length &&
            this.song.autoPilotLoop)) &&
        (this.currentTrackType == TrackType.MAIN ||
          this.currentTrackType == TrackType.FILL)
      ) {
        // If there isn't a transition between parts, start and stop a
        // transition quickly to move to the next part
        this.triggerStartTransition(127); // doesn't need , true for triggeredByAutopilot, since there is no transition track here.
        this.triggerEndTransition();
        this.numberOfFillsPlayed = 0;
      } else if (this.currentPartIndex === this.song.parts.length - 1) {
        // If we are at the end of the song, play the outro
        this.triggerOutro(true);
      } else {
        console.warn('What in the world happened here?', this.currentPartIndex, this.song.parts.length, this.song.autoPilotLoop)
      }
    }
  }

  trackStart = 0;
  trackEnd = 0;
  playTrack(
    track: SongTrack,
    type: TrackType,
    loop: boolean = false,
    playFrom?: number,
    startOffset?: number,
  ) {
    playFrom = playFrom ?? 0;
    startOffset = startOffset ?? 0;
    this.scheduleEvents(track, type, loop, playFrom, startOffset);
  }

  queueFillTypeTrack(
    type: TrackType,
    track: SongTrack,
    loop: boolean,
    playImmediately?: boolean,
    followedBy?: QueuedTrack,
    triggeredByAutopilot = false
  ) {
    if (!this.currentTrack) return;

    const currentTime = this.audioContext.currentTime;

    const currentTrack = this.currentTrack;
    let startOffset = type === TrackType.MAIN ? 0 : track.trigPos;
    const timePerBar =
      (this.trackEnd - this.trackStart) / currentTrack.barCount;
    const elapsedTime = (currentTime - this.trackStart) % timePerBar;
    const currentTick = Math.round(
      currentTrack.barLength - (timePerBar - elapsedTime) * this.ticksPerSecond,
    );
    let ticksToStartOffset = startOffset - currentTick;
    if (ticksToStartOffset < 0) {
      startOffset = startOffset - ticksToStartOffset;
    }
    let playFrom =
      currentTime + (startOffset - currentTick) / this.ticksPerSecond;
    if (playImmediately) {
      playFrom = 0;
      startOffset = 0;
    }
    if (triggeredByAutopilot) {
      startOffset = track.trigPos // Because of the way ticks work with bbmo (where tick() isn't called every tick, but more like every 10-30 ticks, which is why we need things like transitionDelay or outroDelay in setupAutopilotForSinglePart), sometimes a transition might start a little later than it should, causing the first notes to be missed. This fixes that, and says if autopilot triggers the transition, don't start it late, just start it from the beginning (so no beginning notes are missed)
    }
    this.queue(type, track, { loop, playFrom, startOffset, followedBy });
  }

  @tracked currentPartIndex = 0;
  @tracked currentFillIndex = -1;
  @action
  triggerFill(apEvent = false) {
    if (this.currentTrackType === TrackType.TRANSITION) {
      this.transitionToPart = this.transitionFromPart;
      return;
    }
    if (this.currentTrackType === TrackType.INTRO) return;
    if (this.currentTrackType === TrackType.FILL) return;
    if (this.currentTrackType === TrackType.OUTRO) return;
    const wasPaused = this.currentTrackType === TrackType.PAUSED;
    this.unpause();

    // Reset any pending transition
    this.transitionToPart = this.currentPartIndex;

    const part = this.currentPart;
    if (part) {
      let fillIndex = this.currentFillIndex;
      if (part.shuffleFills) {
        fillIndex = Math.floor(Math.random() * part.fills.length);
      } else {
        fillIndex++;
        if (apEvent) {
          let startIndex = fillIndex;
          while (this.song!.info!.parts[this.currentPartIndex]!.drumFillPlayAt[fillIndex] === 0) {
            fillIndex++;
            if (fillIndex >= part.fills.length) {
              fillIndex = 0;
            }
            if (fillIndex === startIndex) {
              // We have looped through all fills
              console.warn("how did this happen?", fillIndex, startIndex, "part", part, "fills", part.fills);
              break;
            }
          }
        }
        if (fillIndex >= part.fills.length) {
          fillIndex = 0;
        }
      }

      const fill = part.fills[fillIndex];
      if (fill) {
        this.playFill(fill, wasPaused);
      }
      this.currentFillIndex = fillIndex;
    }
  }

  /**
   * Queue fill settings:
   * - Immediately (last half beat == next measure)
   * - Next half beat
   * - Next Beat
   * - Next Measure
   */
  playFill(fillTrack: SongTrack, immediately = false) {
    if (!this.currentTrack) return;
    this.currentTrackType = TrackType.FILL;

    let offset = 0;

    if (this.song.autoPilotEnabled) {
      offset =
        this.currentPart!.main!.barLength *
          (this.currentPart!.info.drumFillPlayAt[this.numberOfFillsPlayed]! - 1) +
        fillTrack.barCount * fillTrack.barLength;
      offset %=
        this.currentPart!.main!.barLength * this.currentPart!.main!.barCount;
    }
    this.queueFillTypeTrack(TrackType.FILL, fillTrack, false, immediately, {
      type: TrackType.MAIN,
      track: this.currentPart!.main!,
      loop: true,
      startOffset: offset,
    });
  }

  transitionFromPart = 0;
  transitionToPartRecordKeeping = 0;
  transitionToPart = 0;
  transitionTrackPlaying = false;
  @action
  triggerStartTransition(partNumber: number, triggeredByAutopilot = false) {
    if (this.currentTrackType === TrackType.TRANSITION) return;
    if (this.currentTrackType === TrackType.PAUSED) {
      this.unpause();
      this.stop();
      return;
    }

    let partIndex = 0;
    // Next part
    if (partNumber == 127) {
      partIndex = this.currentPartIndex + 1;
      if (partIndex >= this.song.parts.length) {
        partIndex = 0;
      }
      // Previous part
    } else if (partNumber == 126) {
      partIndex = this.currentPartIndex - 1;
      if (partIndex < 0) {
        partIndex = this.song.parts.length - 1;
      }
    } else {
      partIndex = partNumber - 1;
    }

    this.transitionFromPart = this.currentPartIndex;
    this.transitionToPartRecordKeeping = constrainValue(
      partIndex,
      0,
      this.song.parts.length - 1,
    );
    const part = this.currentPart;
    this.currentTrackType = TrackType.TRANSITION;
    if (part?.transition) {
      this.transitionTrackPlaying = true;
      this.#startTransition(part.transition, triggeredByAutopilot);
    }
  }

  #startTransition(transitionTrack: SongTrack, triggeredByAutopilot = false) {
    this.queueFillTypeTrack(TrackType.TRANSITION, transitionTrack, true, undefined, undefined, triggeredByAutopilot);
  }

  @action
  triggerEndTransition() {
    this.transitionToPart = this.transitionToPartRecordKeeping;
    if (!this.isPlaying) {
      this.start(this.transitionToPart);
    }
    if (!this.currentTrack) return;
    this.apEvents = []; // clear events so the previous part doesn't influence next part (eg if you manually transition early)
    const nextPart = this.song.parts.at(this.transitionToPart);
    if (nextPart?.main) {
      this.currentFillIndex = -1;
      if (
        this.song.autoPilotEnabled &&
        nextPart.info.autoPilotEnabled
      ) {
        // set up autopilot each time a part is transitioned to
        // that way, even if you manually transition, it is set up correctly
        const timePerTick =
          (this.trackEnd - this.trackStart) / this.currentTrack.totalTicks;
        const currentTime = this.audioContext.currentTime;
        const elapsedTime = currentTime - this.trackStart;
        let currentTick = Math.round(elapsedTime / timePerTick);
        // This is to find the total number of ticks or the current part that is playing
        // If there is a transition we only want to take the total number of ticks per bar because totalTicksPerSong
        // will account for the other ticks in other measures
        // If there is no transition then we want to take the total ticks of the current track
        const currentTrackBarLength = this.currentPart!.transition
          ? this.currentPart!.transition.barLength
          : this.currentTrack.barLength;
        // calculate number of ticks until the part changes (until transition ends). Pass that to set up next part autopilot
        const ticks = this.totalTicksPerSong! 
        + currentTrackBarLength // find the end of the current bar, the point the transition will occur
        - currentTick % currentTrackBarLength; //this.totalTicksPerSong may be in the middle of a bar, so we only want to add the amount that's left
        this.setupAutoPilotForSinglePart(this.transitionToPart, ticks);
      }
      this.queue(TrackType.MAIN, nextPart.main, {
        loop: true,
        nextBar: true,
      });
    }
    this.transitionTrackPlaying = false;
  }

  private get currentPart() {
    return this.song.parts.at(this.currentPartIndex);
  }

  triggerOutro(fromAutopilot: boolean = false) {
    this.manualStopWhileAutoPilot = !fromAutopilot;
    this.numberOfFillsPlayed = 0;
    if (this.song.outro && this.song.outro.main) {
      this.unpause();
      this.playOutro(this.song.outro.main);
    } else {
      this.stop();
    }
  }

  playOutro(outroTrack: SongTrack) {
    this.currentTrackType = TrackType.OUTRO;
    this.queueFillTypeTrack(TrackType.OUTRO, outroTrack, false);
  }

  triggerFootSwitchLeft() {
    const part = this.currentPart;
    if (part && part.accentFileName) {
      const effect = this.projectManager.findEffect(part.accentFileName);
      if (effect) {
        playAccent(
          this.audioContext,
          this.drumsetVolume,
          effect,
          part.accentVolume,
        );
      }
    }
  }

  triggerFootSwitchRight() {
    clearTimeout(this.loopTimer);
    if (this.currentTrackType === TrackType.PAUSED) {
      this.unpause();
      this.playTrack(this.currentTrack!, TrackType.MAIN, true);
    } else {
      this.pause();
    }
  }

  pause() {
    if (this.currentTrack && this.currentTrackType !== TrackType.PAUSED) {
      this.playingEvents.forEach(([, note]) => note.stop());
      this.drumsetVolume.gain.setValueAtTime(0, this.audioContext.currentTime);
      this.currentTrackType = TrackType.PAUSED;
      this.playTrack(this.currentTrack!, TrackType.PAUSED, true);
    }
  }

  unpause() {
    if (this.currentTrackType === TrackType.PAUSED) {
      this.playingEvents.forEach(([, note]) => note.stop());
      const drumsetGain = (this.drumset?.volume ?? 100) / 100;
      this.drumsetVolume.gain.setValueAtTime(
        drumsetGain,
        this.audioContext.currentTime,
      );
      this.currentTrackType = TrackType.MAIN;
    }
  }

  playingEvents = new List<[number, AudioBufferSourceNode]>();
  loopTimer?: number;
  trackMarkerTimer?: number;
  scheduleEvents(
    track: SongTrack,
    type: TrackType,
    loop: boolean,
    playStart = 0,
    playFromTick = 0,
    playToTick = -1,
    eventStartIndex = -1,
  ) {
    if (!track) {
      throw new Error('No track to play');
    }
    if (!this.isPlaying) return;
    // Store this argument because we want to pass it through unmodified when buffering the next part of the track
    const playToTickArg = playToTick;

    const events = track.events;
    const time = this.audioContext.currentTime;

    if (playStart === 0) {
      // If there is no offset specified, then we start immediately at the current time
      playStart = time;
    }

    // How many MIDI events to buffer
    const eventsBuffer = track.barLength * 1;

    // What tick will we pay to in this buffer
    playToTick = Math.min(
      track.totalTicks,
      playToTick == -1 ? playFromTick + eventsBuffer : playToTick,
    );

    // How long is the buffer (before we allocate extra events if needed)
    const duration = (playToTick - playFromTick) / this.ticksPerSecond;

    // If we are playing to the end of the track, then we need to add the extra events (for a fill)
    if (playToTick == track.totalTicks) {
      playToTick += track.extraTicks;
    }

    // We’ve started the track from the beginning
    if (this.currentTrack !== track || playFromTick === 0) {
      // Reset track markers
      this.trackMarkerTimer = setTimeout(
        () => {
          if (type === TrackType.MAIN) {
            // Set the main part
            this.currentPartIndex = this.transitionToPart;
          }
          this.currentTrack = track;
          this.currentTrackType = type;
          // We need to calculate track start from track end because the track might start mid-way through
          this.trackEnd =
            playStart + (track.totalTicks - playFromTick) / this.ticksPerSecond;
          this.trackStart =
            this.trackEnd - track.totalTicks / this.ticksPerSecond;
        },
        (playStart - time) * 1000,
      );
    }

    // Determine the range of events to play
    let eventEndIndex = events.length;
    for (
      let i = Math.max(eventStartIndex, 0), ii = events.length;
      i < ii;
      i++
    ) {
      const trigPos = type === TrackType.MAIN ? 0 : track.trigPos;
      const tick = trigPos + events[i]!.tick;
      if (eventStartIndex < 0 && tick >= playFromTick) {
        eventStartIndex = i;
      }
      if (tick >= playToTick) {
        eventEndIndex = i;
        break;
      }
    }

    // If we have no notes to play, then we’re done
    if (eventStartIndex < 0) {
      eventStartIndex = eventEndIndex;
    }

    // Set a timer to revisit this function when the buffer is done
    this.loopTimer = setTimeout(
      () => {
        playFromTick = playFromTick + eventsBuffer;
        if (playFromTick > track.tickCount) {
          playFromTick = 0;
          eventEndIndex = 0;
        }

        let shouldLoop = loop;
        // We don’t want to loop if there is a queued track about to play
        if (this.trackQueue.first && !this.song.autoPilotEnabled) {
          shouldLoop = false;
        }

        // playFromTick is greater than 0 if we’re buffering the next part of the track
        if (playFromTick > 0 || shouldLoop) {
          if (this.song.autoPilotEnabled && this.currentPartIndex == this.song.parts.length - 1 && this.transitionToPart == 0) {
            const ticksIntoPart = this.totalTicksPerSong! - this.tickAtWhichPartStarted;
            const barLength = this.currentPart!.tracks[0]!.barLength;
            const partDuration = this.currentPart!.info.playFor * barLength;
            if (ticksIntoPart >= partDuration - barLength // only if we're in the last bar
              && this.song.autoPilotLoop) { // only if the song will loop 
              // above two checks added because of issue #703
              // below return originally here because of issue #410,
              return; // this is to solve an issue where you'd hear the very first note of the transition as the song loops (when loop song is checked)
            }
          }
          this.scheduleEvents(
            track,
            type,
            loop,
            playStart + duration,
            playFromTick,
            playToTickArg,
            eventEndIndex,
          );
        }
      },
      // Set the timeout to buffer the next part of the track in the last buffered beat
      (playStart + duration - time - this.secondsPerBeat) * 1000,
    );

    // If we’re buffering beyond the first part of track, then we don’t need the fill choke groups any more
    if (eventStartIndex > 0) {
      this.clearFillChokeGroups();
    }

    let event, onWhen, offWhen, instrument, note;
    // Loop through the events to be played
    for (let i = eventStartIndex; i < eventEndIndex; i++) {
      event = events[i]!;
      const trigPos = type === TrackType.MAIN ? 0 : track.trigPos;
      const tick = trigPos + event.tick;
      // Determine when to play and stop the note
      onWhen = playStart + (tick - playFromTick) / this.ticksPerSecond;
      // Will be used for non-percussion instruments
      offWhen = onWhen + event.length / this.ticksPerSecond;

      instrument = this.drumset?.instruments[event.note];
      if (instrument) {
        // Stop all instruments in the same choke group
        if (instrument.chokeGroup > 0) {
          const chokeGroup = this.getChokeGroup(instrument.chokeGroup);
          chokeGroup.choke(onWhen, instrument.name);
        }

        // Check if this note should be excluded because of a fill choke group
        if (!this.shouldNoteBeExcluded(instrument, onWhen)) {
          note = playInstrument(
            this.audioContext,
            this.drumsetVolume,
            instrument,
            onWhen,
            offWhen,
            event.velocity,
          );
        }

        if (note) {
          // Add the note to the choke group
          if (instrument.chokeGroup > 0) {
            this.addToChokeGroup(instrument.chokeGroup, note, instrument.name);
          }
          // Store the note so we can stop it when needed
          this.playingEvents.push([onWhen, note]);
        }

        // If we’re dealing with extra ticks for a fill, add them to a fill choke group
        if (tick >= track.totalTicks && instrument.fillChokeGroup > 0) {
          this.addToFillChokeGroup(instrument.fillChokeGroup, onWhen);
        }
      }
    }

    // Quick cleanup
    this.playingEvents.dropWhile(
      ([noteOn]) => noteOn < this.audioContext.currentTime,
    );
  }

  shouldNoteBeExcluded(instrument: Instrument, onWhen: number) {
    if (instrument.fillChokeGroup === 0) return false;

    const fillChokeGroup = this.getFillChokeGroup(instrument.fillChokeGroup);
    let delaySeconds = this.secondsPerSixteenth;
    if (instrument.fillChokeDelay === 0) {
      // 1/4
      delaySeconds = this.secondsPerSixteenth * 4;
    } else if (instrument.fillChokeDelay === 1) {
      // 1/8
      delaySeconds = this.secondsPerSixteenth * 2;
    } else {
      // 1/16
    }
    return fillChokeGroup.some((when) => {
      return onWhen >= when - delaySeconds && onWhen < when + delaySeconds;
    });
  }
}

declare module '@ember/service' {
  interface Registry {
    'virtual-pedal': VirtualPedalService;
  }
}
